/**
 * Web's receive-side save handoff — the `ReceiveDownloads` seam
 * `receiveDeps.web.ts` wires into `receiveHost.ts`'s `ReceiveHostDeps.downloads`.
 *
 * Controller decision (docs/file-transfer-resume.md, M4): OPFS staging +
 * download handoff on BOTH the extension and web paths (uniform,
 * resume-compatible — the finished OPFS `File` from `output.finalize()` is
 * the same regardless of surface). `showSaveFilePicker` is used as the FINAL
 * save step where available (lets the user pick a destination, like the
 * extension's `browser.downloads` "Save As"); the fallback for every other
 * browser (Firefox, Safari, older Chromium) is an `objectURL` + anchor
 * `download` click — the universal web mechanism, landing in the browser's
 * normal Downloads folder.
 */

import type { FinalizedFile, ReceiveHandoffDisposition } from "@reflecto/client-core";

export interface WebDownloads {
  /** Takes the engine's `FinalizedFile` union verbatim (NOT a narrowed `File`) — a method
   * parameter would be checked bivariantly, so a narrowed signature here would compile while
   * silently claiming to handle a value this surface cannot. Web outputs are OPFS-backed and
   * always yield a real `File`; the non-`Blob` arm is rejected loudly below. */
  download(file: FinalizedFile, suggestedName: string): Promise<ReceiveHandoffDisposition>;
}

interface SaveFilePickerWindow extends Window {
  showSaveFilePicker(options?: { suggestedName?: string }): Promise<FileSystemFileHandle>;
}

/** Feature-detect the File System Access API's save picker (Chromium-only today). */
function hasShowSaveFilePicker(w: Window): w is SaveFilePickerWindow {
  return typeof (w as Partial<SaveFilePickerWindow>).showSaveFilePicker === "function";
}

async function saveViaFilePicker(
  file: File,
  suggestedName: string,
  w: SaveFilePickerWindow
): Promise<void> {
  const handle = await w.showSaveFilePicker({ suggestedName });
  const writable = await handle.createWritable();
  await writable.write(file);
  await writable.close();
}

/**
 * Universal fallback: objectURL + a synthetic anchor click — every browser's normal
 * download path. Inherently DETACHED: the click returns immediately while the browser
 * reads the blob in the background, and there is no completion event to await. The
 * caller must therefore keep the staged OPFS copy alive; deleting it mid-read throws
 * NotFoundError and truncates the save.
 */
export function saveViaAnchorDownload(file: File, suggestedName: string): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on a delay — revoking immediately can race the download start in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export const webDownloads: WebDownloads = {
  async download(file, suggestedName) {
    // Unreachable in production: this surface's `ReceiverOutput` is the OPFS worker client, whose
    // `finalize()` resolves `handle.getFile()` — always a real `File`. The path-carrier arm of
    // `FinalizedFile` belongs to the CLI's rename-based adapter, which never reaches this module.
    // Explicit rather than a cast, so a future output adapter that returns a carrier fails here
    // instead of at `URL.createObjectURL(<plain object>)` with an opaque browser error.
    if (!(file instanceof Blob)) {
      throw new TypeError("web downloads handoff: expected a Blob-backed finalized file");
    }
    if (hasShowSaveFilePicker(window)) {
      try {
        // Fully written and closed before this resolves — a confirmed handoff.
        await saveViaFilePicker(file, suggestedName, window);
        return "durable";
      } catch (err) {
        // AbortError = the user cancelled the picker — respect that; do NOT
        // silently fall back to an unrequested anchor download. Any OTHER
        // failure (unsupported context, permission denied, etc.) falls
        // through to the universal anchor path below.
        if (err instanceof DOMException && err.name === "AbortError") {
          throw err;
        }
      }
    }
    saveViaAnchorDownload(file, suggestedName);
    return "detached";
  },
};
